import {useTaggingClick} from "@/hooks/common/useTagging";
import {getCookie, getTarget, onClickSSO} from "@COMPONENTS/common/commonUtil";
import ErrorBoundary from "@COMPONENTS/common/error/ErrorBoundary";
import SSOLink from "@COMPONENTS/common/ssoLink";
import Recently from "@COMPONENTS/layout/floating/Desktop/Recently";
import GNB from "@COMPONENTS/layout/header/Desktop/Gnb";
import SearchArea from "@COMPONENTS/layout/header/Desktop/SearchArea";
import publicRuntimeConfig from "@FRAMEWORK/config/public";
import * as style from "@FRAMEWORK/ui/desktop/header/index";
import {Accessibility} from "@FRAMEWORK/ui/desktop/text";
import useCustomerMe from "@QUERIES/account/useCustomerMe";
import useGlobalOpt from "@QUERIES/bff/global/useGlobalOpt";
import useGetRecently from "@QUERIES/essential/user/useGetRecently";
import {useRouter} from "next/router";
import {useEffect, useRef, useState} from "react";

const HeaderComponent = () => {
	const router = useRouter();

	/** 로그인 정보 */
	const signin = useCustomerMe({throwOnError: false});
	const webCustNo = signin.data?.body.webCustNo || "";
	const custNmKor = signin.data?.body.custNmKor || "";
	const isSignin = signin.data?.body.nonUser === false;

	/** 최근 본 관련 */
	const userId = getCookie("userId") || (window.ensureUserHasUniqueId ? window.ensureUserHasUniqueId() : "");
	const recentlyListFetch = useGetRecently(userId, webCustNo, {
		enabled: !signin.isFetching && !!userId,
		throwOnError: false,
	});
	const recently = recentlyListFetch.data?.body?.productResultList?.at(0) || null;

	const [recentlyOpen, setRecentlyOpen] = useState(false);
	const [recentlyShow, setRecentlyShow] = useState(false);
	/** routing 시 최근 본 상품 닫기  */
	const handleRouteChange = () => {
		setRecentlyShow(false);
		setRecentlyOpen(false);
	};

	useEffect(() => {
		router.events.on("routeChangeComplete", handleRouteChange);
		return () => {
			router.events.off("routeChangeComplete", handleRouteChange);
		};
	}, [router]);

	/** 운영 메뉴 관련 */
	const globalOptFetch = useGlobalOpt();
	const globalOpt = globalOptFetch.data?.body;

	/** publish */
	const [showTooltip, setShowTooltip] = useState(false);
	const [isDim, setIsisDim] = useState(false);
	const handleDim = (is: boolean) => setIsisDim(is);

	const tooltipRef = useRef<HTMLDivElement>(null);
	const loginRef = useRef<HTMLLIElement | null>(null);

	// 로그인시 나오는 툴팁
	const handleClickOutside = (event: MouseEvent) => {
		const target = event.target as Element | null;

		if (target && !tooltipRef.current?.contains(target) && !loginRef.current?.contains(target)) {
			setShowTooltip(false);
		}
	};

	useEffect(() => {
		if (showTooltip) {
			document.addEventListener("mousedown", handleClickOutside);
		} else {
			document.removeEventListener("mousedown", handleClickOutside);
		}

		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [showTooltip]);

	const handleClick = () => {
		setShowTooltip(prev => !prev);
	};

	const {taggingClick} = useTaggingClick("pkg_select_icon_main");

	return (
		<>
			{/* 최근 본 상품 */}
			<Recently
				open={recentlyOpen}
				show={recentlyShow}
				closeShow={() => setRecentlyShow(false)}
				closeOpen={() => setRecentlyOpen(false)}
			/>
			<style.HeaderWrap>
				{/* TopBanner */}
				{/* <TopBanner /> */}
				<style.Container>
					<style.SkipNavi>
						<a href="#content">본문 바로가기</a>
						<a href="#gnb">카테고리 메뉴 바로가기</a>
						<a href="#gnb-login">로그인 메뉴 바로가기</a>
					</style.SkipNavi>
					<style.Gnb>
						<style.TopNavWrap>
							<div>
								<h1>
									<SSOLink href={publicRuntimeConfig.ENV_PORTAL_URL}>
										<img src="https://cimgcdn.ybtour.co.kr/common/logo/logo_ev.png" alt="노랑풍선" />
									</SSOLink>
								</h1>
								{/* 검색 영역 */}
								<SearchArea />
							</div>
							<style.UtileLinks>
								<ul>
									{/* 운영 메뉴 */}
									{globalOpt &&
										globalOpt.map((item, index) => (
											<li key={index}>
												<SSOLink
													href={item.pcMenuUrl}
													target={getTarget(item.webMenuTarget)}
													onClick={() => taggingClick(item)}
												>
													{item.pcMenuNm}
												</SSOLink>
											</li>
										))}
								</ul>
								<style.BtnGroup>
									<style.BtnMypage
										href={`${publicRuntimeConfig.ENV_PACKAGE_URL}/mypage/index.yb`}
										onClick={e => {
											const url = `${publicRuntimeConfig.ENV_PACKAGE_URL}/mypage/index.yb`;
											onClickSSO(e, url);
											if (!isSignin) LoginUtil.login(url);
										}}
									>
										<Accessibility>마이페이지</Accessibility>
									</style.BtnMypage>
									<style.BtnRecent
										href="#"
										$hasRecent={!!recently}
										onClick={() => {
											setRecentlyOpen(true);
											setRecentlyShow(true);
										}}
									>
										<Accessibility>최근본</Accessibility>
										{recently?.imgUrl && <img src={recently.imgUrl} alt="상품 이미지" />}
									</style.BtnRecent>
								</style.BtnGroup>
							</style.UtileLinks>
						</style.TopNavWrap>
						{/* GNB */}
						<GNB handleDim={handleDim} />
						{/* GNB */}
						<style.GnbUtil id="gnb-login">
							<style.GnbUtilWrap>
								<ul>
									{isSignin ? (
										<>
											<style.Login onClick={handleClick} $isShow={showTooltip} ref={loginRef}>
												{custNmKor} 님
												{showTooltip && (
													<style.LoginTooltip ref={tooltipRef}>
														<SSOLink href={`${publicRuntimeConfig.ENV_ACCOUNTS_DOMAIN}/settings/profile`}>
															개인정보 수정
														</SSOLink>
														<SSOLink href={`${publicRuntimeConfig.ENV_ACCOUNTS_DOMAIN}/settings/password`}>
															비밀번호 변경
														</SSOLink>
														<SSOLink href={`${publicRuntimeConfig.ENV_ACCOUNTS_DOMAIN}/settings/social`}>
															SNS 연결 설정
														</SSOLink>
													</style.LoginTooltip>
												)}
											</style.Login>
											<li>
												<a
													href="#"
													onClick={e => {
														e.preventDefault();
														LoginUtil.logout();
													}}
												>
													로그아웃
												</a>
											</li>
										</>
									) : (
										<>
											<li>
												<a
													href="#"
													onClick={e => {
														e.preventDefault();
														LoginUtil.login();
													}}
												>
													로그인
												</a>
											</li>
											<li>
												<SSOLink href={`${publicRuntimeConfig.ENV_ACCOUNTS_DOMAIN}/signUp`}>회원가입</SSOLink>
											</li>
										</>
									)}
									<li>
										<SSOLink href={`${publicRuntimeConfig.ENV_PACKAGE_URL}/mypage/resvInfo.yb`}>예약확인/결제</SSOLink>
									</li>
									<li>
										<SSOLink href={`${publicRuntimeConfig.ENV_PACKAGE_URL}/cs/main.yb`}>고객센터</SSOLink>
									</li>
								</ul>
							</style.GnbUtilWrap>
						</style.GnbUtil>
					</style.Gnb>
				</style.Container>
				{isDim && <style.Dim />}
			</style.HeaderWrap>
		</>
	);
};

const Header = () => (
	<ErrorBoundary logMsg="Header Error">
		<HeaderComponent />
	</ErrorBoundary>
);

export default Header;
